/* Paramètres › Compte et accès (Scope v5 §3 Compte · batch 22 · Roadmap 0.4).

   Who signs in, with which role, the invitations still open, and the plan —
   BI-08 / BI-09 / BI-14 re-homed under one page. People as people are not
   here: prénom, PIN and personal QR live in Suivre › Équipe.

   The logins are not a second list of people. The owner and the admins come
   from the account (they need not work at the till); everyone else with a
   login is a member of @/lib/team whose `access` is a role — so a member given
   access shows here and in Équipe from the same record. The four-role rule is
   BI-08's, imported: ROLE_LABEL, unscoped, actsIn, roleRule (MA-19). The tills
   are Équipe's « Vos caisses », registered at the « Valider un cadeau » door (V19).
   MA-23: an open invitation and a role change are two lists, never one « Gérer ».
   MA-21: on the Free plan the card is the plan and its ceiling — no invoice, no
   billing profile (Free is a workspace state, not a Stripe object).
   FQ-I4: « Votre offre » is the Offre page (/parametres/offre); BI-10 opens from « Votre espace ». */
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, KV, List, ListRow, PageHeader, Pill } from '@/ui'
import { useLocations } from '@/lib/api'
import { getPlan } from '@/lib/campaign'
import { ALL, scopedPath, useScope } from '@/lib/scope'
import { getMembers } from '@/lib/team'
import { actsIn, ROLE_LABEL, roleRule, unscoped, type TeamRole } from './api'
import { useBill } from './ParamShell'

interface Login { id: string; name: string; email?: string; role: TeamRole; locations: string[]; member: boolean }

export default function Compte() {
  const b = useBill()
  const nav = useNavigate()
  const [scope] = useScope()
  const { data: places } = useLocations()
  const placeName = (id: string) => places?.find(l => l.id === id)?.name ?? id

  /* the account's unscoped logins (owner, admins) — on day one, the owner alone */
  const account: Login[] = b.team
    .filter(t => unscoped(t.role) && (!b.teamEmpty || t.role === 'owner'))
    .map(t => ({ id: t.id, name: t.name, email: t.email, role: t.role, locations: [], member: false }))
  /* members with a login, from the members seam: a manager's or a collaborator's (the owner and the admins are the
     account's rows above; with an API the owner is also a member of the whole workspace, listed once) */
  const members: Login[] = getMembers()
    .filter(m => m.access !== 'none' && !unscoped(m.access))
    .map(m => ({ id: m.id, name: m.firstName, role: m.access as TeamRole, locations: m.locationId ? [m.locationId] : [], member: true }))
  const logins = [...account, ...members].filter(l => actsIn(l.role, l.locations, scope, ALL))
  const where = (l: Login) => unscoped(l.role) ? 'tout' : l.locations.map(placeName).join(' · ') || 'aucun'
  const pending = b.teamEmpty ? [] : b.pending.filter(r => !r.role || actsIn(r.role, r.locations ?? [], scope, ALL))
  const invitations = pending.filter(r => r.kind === 'invitation')
  const roles = pending.filter(r => r.kind === 'role')
  const gerer = () => nav(scopedPath('/parametres/equipe/gestion', scope))
  const free = getPlan() === 'free'
  const offre: Array<[string, React.ReactNode]> = [
    ['Offre', <Pill key="o" tone={b.planBadge.tone}>{b.planBadge.label}</Pill>],
    ['Participations ce mois', `${b.usage.valid}${b.usage.location}`],
  ]
  if (!free) offre.push(['Dernière facture', b.invoices[0] ? `${b.invoices[0].date} · ${b.invoices[0].amount}` : '—'])

  return (
    <section data-frame="CA-01" className="page" style={{ maxWidth: 980 }}>
      <PageHeader title="Compte et accès"
        description={<>Qui se connecte, avec quel rôle, et votre offre. Les personnes elles-mêmes — prénom, code caisse, QR — sont dans <Link to="/equipe">Équipe</Link>.</>}
        actions={<Button variant="primary" size="sm" onClick={() => nav(scopedPath('/parametres/equipe/inviter', scope))}>Inviter</Button>}
        next={<Link to="/parametres/integrations">vos intégrations</Link>} />

      <div className="sect">
        <p className="t-eyebrow">Connexions</p>
        <div className="tablewrap">
          <table className="tbl">
            <thead><tr><th>Personne</th><th>Rôle</th><th>Établissements</th><th></th></tr></thead>
            <tbody>
              {logins.map(l => (
                <tr key={l.id}>
                  <td><strong>{l.name}</strong>{l.email && <><br /><span className="f-help">{l.email}</span></>}</td>
                  <td>{ROLE_LABEL[l.role]}</td>
                  <td>{where(l)}</td>
                  <td>{l.member
                    ? <Button size="sm" variant="ghost" onClick={() => nav(`/equipe?membre=${l.id}`)}>Dans Équipe</Button>
                    : l.role === 'owner' ? <Pill tone="ok">vous</Pill> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {roleRule((places ?? []).filter(l => l.status !== 'inactive').length).map(line => <p key={line} className="f-help" style={{ margin: 0 }}>{line}</p>)}
      </div>

      <div className="sect" data-testid="invitations">
        <p className="t-eyebrow">Invitations</p>
        {invitations.length === 0
          ? <p className="f-help" style={{ margin: 0 }}>Aucune invitation en attente.</p>
          : (
            <List>
              {invitations.map(r => (
                <ListRow key={r.id} title={r.title} meta={r.meta}
                  end={<Button size="sm" variant="quiet" onClick={gerer}>Gérer</Button>} />
              ))}
            </List>
          )}
      </div>

      {roles.length > 0 && (
        <div className="sect" data-testid="roles">
          <p className="t-eyebrow">Rôles</p>
          <List>
            {roles.map(r => (
              <ListRow key={r.id} title={r.title} meta={r.meta}
                end={<Button size="sm" variant="quiet" onClick={gerer}>Gérer</Button>} />
            ))}
          </List>
        </div>
      )}

      <div className="sect">
        <p className="t-eyebrow">Offre et facturation</p>
        <Card data-testid="offre">
          <KV rows={offre} />
        </Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Button size="sm" onClick={() => nav('/parametres/offre')}>Votre offre</Button>
          {!free && <>
            <Button size="sm" variant="quiet" onClick={() => nav('/parametres/factures')}>Factures</Button>
            <Button size="sm" variant="quiet" onClick={() => nav('/parametres/profil')}>Profil de facturation</Button>
          </>}
        </div>
      </div>

      {/* FQ-I4 · BI-10 left the billing strip: its door is here, with the account */}
      <div className="sect" data-testid="espace">
        <p className="t-eyebrow">Votre espace</p>
        <p className="f-help" style={{ margin: 0 }}>Export des données, rétention, sécurité, fermeture du compte, panier moyen.</p>
        <div><Button size="sm" variant="quiet" onClick={() => nav(scopedPath('/parametres/espace', scope))}>Ouvrir</Button></div>
      </div>
    </section>
  )
}
